<template>
  <div>

    <div v-if="!show404 && !show403">
      <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

      <div class="card" id="update_basic_info">
        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <h4 qid="p-view-page-title" class="mb-3">
                {{ policy.name }}
              </h4>
              <div class="mb-2">
                <span class="cursor-pointer task-label-open mr-2"
                      v-if="getTask('update_basic_info')"
                >
                  <UpdateSingleTaskModal
                      :task="myTasks[0]"
                      @reloadContent="reloadAll"
                      :editIcon="true"
                  ></UpdateSingleTaskModal>
                </span>
                <span v-if="getGuide('update_basic_info')">
                  <GuideButton :guide="getGuide('update_basic_info')"></GuideButton>
                </span>
              </div>
              <div class="mb-2">
                <span v-if="policy.users && policy.users.length" class="mr-2" qid="p-owners-list">
                  {{ $t('table.owners') }}:
                </span>
                <span class="mr-1" v-for="user in policy.users">
                  <Avatar :object="user" size="xs"></Avatar>
                </span>
                <span v-if="policy.category" class="mr-2" qid="p-owners-list">
                  {{ $t('maintenance.policies.table.category') }}:
                </span>
                <span class="mr-1 text-normal" v-if="policy.category">
                  <font-awesome-icon icon="shapes"/>
                  {{ $t('maintenance.policies.category.' + $options.filters.slugify(policy.category)) }}
                </span>
                <span v-if="policy.organization" class="mr-2" qid="p-owners-list">
                  {{ $t('administration.users.create.organization_label') }}:
                </span>
                <span class="mr-1 text-normal" v-if="policy.organization">
                  <font-awesome-icon icon="sitemap"/>
                  {{ policy.organization.name }}
                </span>
                <span v-if="policy.tags_translated && policy.tags_translated.length" class="mr-2 ml-2">
                  {{ $t('table.tags') }}:
                </span>
                <span v-if="policy.tags_translated && policy.tags_translated.length" qid="activity-tags-list">
                  <span class="badge" v-for="tag in policy.tags_translated" :title="tag.name_translated">
                    <font-awesome-icon icon="tag"/>
                    {{ tag.name_translated | truncate(35) }}
                  </span>
                </span>
              </div>
            </div>

            <div class="col-md-6 text-right" qid="view-p-modified">
              <div class="mb-4">
                  <span class="h6 ml-3">
                  <span v-if="isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], policy) && (!getClient.parent || (getClient.parent && policy.partition))"
                        class="btn btn-edit"
                        qid="edit-p-button"
                        @click="showEditPModal(policy)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.POLICY_DELETE, permissions.POLICY_DELETE_OWN], policy) && (!getClient.parent || (getClient.parent && policy.partition))"
                        class="btn btn-delete"
                        qid="delete-p-button"
                        @click="showConfirmDeleteModal(policy)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </span>
              </div>
              <div>
                <span>{{ $t('table.last_updated') }}</span>
                <span class="ml-2 text-muted small" qid="view-p-modified-date">
                  {{ policy.updated_at | formatDate }}
                </span>
                <Avatar v-if="policy.updated_by" :object="policy.updated_by" size="xs" class="ml-3 mr-2"></Avatar>
                <span v-if="policy.updated_by" qid="view-p-modified-by">
                  {{ policy.updated_by.name }}
                </span>
              </div>
            </div>

          </div>
        </div>
      </div>

      <b-tabs class="mt-3" v-model="tabIndex">

        <!-- First tab -->
        <b-tab :title="$t('maintenance.policies.tab.basic_info')" active>
          <div class="card mt-3">

            <!-- Update description -->
            <DescriptionBlock :object="policy" @updated="updatePolicy"></DescriptionBlock>

          </div>

          <br>
          <DocumentsTable :object="policy"></DocumentsTable>

        </b-tab>


        <!-- Relations tab -->
        <b-tab :title="$t('maintenance.third_parties.tab.relations')" class="sub-menu">
          <b-tabs class="menu-smaller">

            <!-- Policies tab -->
            <b-tab :title="$t('maintenance.policies.tab.policies')" class="mt-3">
              <PoliciesTable :object="policy"></PoliciesTable>
            </b-tab>

            <!-- Processes tab -->
            <b-tab :title="$t('maintenance.policies.tab.processes')" class="mt-3">
              <ProcessesTable :object="policy"></ProcessesTable>
            </b-tab>

            <!-- Activities tab -->
            <b-tab :title="$t('maintenance.third_parties.tab.activities')" class="mt-3">
              <ActivitiesTable :object="policy"></ActivitiesTable>
            </b-tab>

            <!-- Systems tab -->
            <b-tab :title="$t('maintenance.third_parties.tab.systems')" class="mt-3">
              <SystemsTable :object="policy"></SystemsTable>
            </b-tab>

            <!-- Generic controls tab -->
            <b-tab :title="$t('maintenance.policies.tab.generic_controls')" class="mt-3">
              <GenericControlsTable :object="policy"></GenericControlsTable>
            </b-tab>

            <!-- Framework tab -->
            <b-tab :title="$t('maintenance.policies.tab.framework')" class="mt-3" v-if="getClient.riskModule">
              <FrameworkControlsTable :object="policy"></FrameworkControlsTable>
            </b-tab>

          </b-tabs>
        </b-tab>

        <!-- Validations tab -->
        <b-tab :title="$t('maintenance.policies.tab.validations')"
               class="mt-3"
        >
          <!-- Validation section component -->
          <Validations
              :object="policy"
              type="policy"
              :checklist="false"
              :permission="['POLICY_VALIDATE', 'POLICY_VALIDATE_OWN']"
              @updated="updateValidation"
          ></Validations>
        </b-tab>

        <!-- history tab -->
        <b-tab :title="$t('maintenance.third_parties.tab.history')" class="mt-3" @click="historyTabClicked">
          <div v-if="showHistoryTab">
            <PolicyHistory :object="policy"></PolicyHistory>
          </div>
        </b-tab>
      </b-tabs>


      <!-- Edit Policy modal template -->
      <b-modal ref="edit-policy" hide-footer>
        <div class="d-block text-center">
          <h4 qid="update-p-title">
            {{ $t('maintenance.policies.update.title') }}
          </h4>
        </div>
        <b-alert v-if="error.length"
                 show
                 variant="danger"
                 qid="edit-p-error"
        >
          {{ error }}
        </b-alert>
        <b-form @submit="editPFormSubmit" qid="edit-p-form">
          <div class="form-container">
            <b-form-group>
              <label qid="edit-p-name-label">
                {{ $t('maintenance.policies.create.name_label') }}
              </label>
              <b-form-input
                qid="edit-p-form-name"
                type="text"
                v-model="pForm.name"
                :maxLength="255"
                required
                :placeholder="$t('maintenance.policies.create.name_placeholder')"
              ></b-form-input>
            </b-form-group>

            <b-form-group>
              <label qid="edit-p-implementation-status-label">
                {{ $t('maintenance.policies.create.category_label') }}
              </label>
              <multiselect
                  v-model="pForm.category"
                  :options="categoryItems"
                  :placeholder="$t('maintenance.policies.create.category_placeholder')"
                  label="name"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  qid="edit-p-form-implementation-status"
              ></multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="create-do-tags-label">
                {{ $t('systems.create.tags_label') }}
              </label>
              <multiselect
                  v-model="selectedTags"
                  :options="tags"
                  :multiple="true"
                  :taggable="true"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  @search-change="loadTags"
                  track-by="name_translated"
                  :placeholder="$t('systems.create.tags_placeholder')"
                  label="name_translated"
                  qid="create-do-tags-select"
                  @tag="addTag"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="create-p-users-label">
                {{ $t('systems.create.owners_label') }}
              </label>
              <multiselect
                  v-model="selectedUsers"
                  :options="users"
                  :multiple="true"
                  :taggable="false"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  @search-change="loadUsers"
                  @remove="removeUserFromDropdown"
                  track-by="slug"
                  :placeholder="$t('systems.create.owners_placeholder')"
                  label="name"
                  qid="create-p-users-select"
              >
                <template slot="tag" slot-scope="props">
                  <span></span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
              <div class="form-control auto-height mt-1" v-if="selectedUsers && selectedUsers.length">
                <div v-for="(user, index) in selectedUsers" class="mt-1 mb-1">
                  <div class="row">
                    <div class="col-sm-1">
                      <font-awesome-icon
                        icon="star"
                        :class="{'color-yellow': user.owner, 'text-light': !user.owner}"
                        class="cursor-pointer"
                        @click="setOwner(index)"
                      />
                    </div>
                    <div class="col-sm-10">
                      <Avatar :object="user" size="xs" :noOwner="true"></Avatar>
                      <span class="ml-2">{{ user.name }}</span>
                    </div>
                    <div class="col-sm-1">
                      <font-awesome-icon
                        icon="times"
                        class="cursor-pointer"
                        @click="removeUser(index)"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <span class="text-light">
                * {{ $t('maintenance.policies.create.owners_tip') }}
              </span>
            </b-form-group>

            <div class="mb-3">
              <label qid="edit-user-phone-label">
                {{ $t('administration.users.create.organization_label') }}
              </label>
              <OrganizationTreeView
                  @updatedCompany="updateFormCompany"
                  :organization="selectedOrganization"
              ></OrganizationTreeView>
            </div>
          </div>
          <div class="text-right">
            <b-button class="mt-3"
                      variant="secondary"
                      @click="hideEditPModal"
                      qid="edit-p-cancel-button"
            >
              {{ $t('maintenance.policies.create.cancel') }}
            </b-button>
            <b-button type="submit"
                      class="mt-3 ml-3"
                      variant="success"
                      :disabled="buttonDisabled"
                      qid="edit-p-submit-button"
            >
              <span v-if="buttonDisabled" class="mr-1">
                <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
              </span>
              {{ $t('maintenance.policies.create.submit') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>


      <!-- Delete Policy confirm modal template -->
      <b-modal ref="delete-policy" hide-footer>
        <div class="d-block text-center">
          <h4 qid="delete-p-title">
            {{ $t('maintenance.policies.delete.title') }}
          </h4>
        </div>
        <h5 class="text-center">
          {{ $t('maintenance.policies.delete.description') }} {{ itemToDelete.name }}
        </h5>
        <h5 class="text-center">
          {{ $t('maintenance.policies.delete.are_you_sure') }}
        </h5>
        <div class="text-center">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideConfirmDeleteModal"
                    qid="delete-p-cancel-button"
          >
            {{ $t('maintenance.policies.delete.cancel') }}
          </b-button>
          <b-button @click="confirmDeleteP(itemToDelete)"
                    class="mt-3 ml-3"
                    variant="success"
                    qid="delete-p-submit-button"
                    v-if="isGranted([permissions.POLICY_DELETE, permissions.POLICY_DELETE_OWN], itemToDelete)"
          >
            {{ $t('maintenance.policies.delete.submit') }}
          </b-button>
        </div>
      </b-modal>

      <ConfirmRemoveAttachment
          :object="selectedFile"
          :show="showDeleteAttachmentModal"
          type="file"
          @closeConfirmModal="closeDeleteAttachmentModal"
      ></ConfirmRemoveAttachment>

      <ConfirmRemoveAttachment
          :object="selectedLink"
          :show="showDeleteLinkModal"
          type="link"
          @closeConfirmModal="closeDeleteLinkModal"
      ></ConfirmRemoveAttachment>
    </div>

    <div v-if="show404">
      <page404></page404>
    </div>

    <div v-if="show403">
      <page403></page403>
    </div>

  </div>
</template>

<script>
import {initialsAvatarUrl} from '@/util/userAvatar.js';
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import roleCheck from "@/util/ACL/roleCheck"

import Validations from '@/views/Validations/Validations.vue';
import Avatar from '@/components/Avatar.vue';
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import OrganizationTreeView from '@/components/organizations/OrganizationTreeView.vue';
import ProcessesTable from '@/views/Policies/ProcessesTable.vue';
import PoliciesTable from '@/views/Policies/PoliciesTable.vue';
import ActivitiesTable from '@/views/Policies/ActivitiesTable.vue';
import SystemsTable from '@/views/Policies/SystemsTable.vue';
import GenericControlsTable from '@/views/Policies/GenericControlsTable.vue';
import FrameworkControlsTable from '@/views/Policies/FrameworkControlsTable.vue';
import DocumentsTable from '@/views/Policies/DocumentsTable.vue';
import DescriptionBlock from '@/views/Policies/DescriptionBlock.vue';
import PolicyHistory from '@/views/Policies/PolicyHistory.vue';
import GuideButton from '@/components/guide/GuideButton.vue';
import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
import page404 from '@/components/404block.vue';
import page403 from '@/components/403block.vue';


export default {
  name: 'PolicyView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.policies.title'),
          to: '/policies'
        }
      ],
      policy: {},
      showInput: false,
      showSingleLinkLabel: true,
      inputLink: '',
      inputLinkName: '',
      itemToDelete: {},
      pForm: {
        name: '',
        description: '',
        category: [],
        tags: [],
        users: [],
        organization_id: '',
        client:'',
      },
      error: '',
      newTags: [],
      categoryInput: '',
      categoryItems: [
        { name: this.$t('maintenance.policies.category.policy'), value: 'policy' },
        { name: this.$t('maintenance.policies.category.procedure'), value: 'procedure' },
        { name: this.$t('maintenance.policies.category.supporting_document'), value: 'supporting_document' }
      ],
      buttonDisabled: false,
      selectedFile: {},
      selectedLink: {},
      showDeleteAttachmentModal: false,
      showDeleteLinkModal: false,
      myGuides: [],
      selectedTags: [],
      selectedUsers: [],
      users: [],
      tags: [],
      myTasks: [],
      selectedOrganization: {},
      descriptionLength: config.descriptionLength,
      showFullDescription: false,
      showDescriptionInput: false,
      processEditDescription: '',
      showHistoryTab: false,
      tabIndex: 0,
      show404: false,
      show403: false,
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    getUser() {
      return this.$store.state.user
    },
    getReloadContent() {
      return this.$store.state.updateContent
    },
    getReloadTasks() {
      return this.$store.state.reloadTasks
    },
    getTabIndex() {
      return this.$store.state.tabIndex
    },
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      let self = this;
      if (newValue && oldValue && newValue.slug && oldValue.slug && newValue.slug !== oldValue.slug) {
        setTimeout(function(){
          if (self.$route.name == 'policies.view') {
            self.goToList()
          }
        }, 800);
      } else {
        this.loadContent();
        this.loadTags();
        this.loadUsers();
      }
    },
    object: function (value) {
      if (value && value.slug) {
        this.policy = value
        this.loadTasks('update_basic_info');
      }
    },
    getReloadContent: function () {
      if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
        if (this.$store.state.updatedObject.id == this.policy.id && this.$store.state.updatedObject.who_am_i == this.policy.who_am_i) {
          this.policy = this.$store.state.updatedObject
        }
      }
    },
    getReloadTasks: function () {
      this.loadTasks('update_basic_info')
    },
    getTabIndex: function (newValue, oldValue) {
      this.handleUpdateTab(newValue)
    }
  },
  components: {
    Validations,
    Avatar,
    ConfirmRemoveAttachment,
    OrganizationTreeView,
    ActivitiesTable,
    ProcessesTable,
    PoliciesTable,
    SystemsTable,
    GenericControlsTable,
    FrameworkControlsTable,
    DocumentsTable,
    PolicyHistory,
    GuideButton,
    DescriptionBlock,
    UpdateSingleTaskModal,
    page403,
    page404
  },
  methods: {
    goToList() {
      this.$router.push('/policies')
    },
    resetFile() {
      let imagefile = document.querySelector('#file');
      imagefile.value = '';
    },
    reloadAll() {
      this.breadcrumbItems.pop();
      this.loadContent()
      this.loadTasks('update_basic_info');
    },
    isOwner(object) {
      let self = this;
      let allow = false;
      let existingUser = _.find(object.users, function(user) {
        return user.id == self.getUser.id
      });

      if (existingUser) {
        allow = true
      } else {
        if (roleCheck('Client admin') || roleCheck('Partition admin') || roleCheck('Validator') || this.getUser.isAdmin) {
          allow = true
        }
      }

      return allow
    },
    getTask(code) {
      let existingTask = _.find(this.myTasks, function(object) {
        return object.code == code
      });
      return existingTask
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Policy' && object.enabled
      });
      return existingGuide
    },
    updatePolicy() {
      this.loadContent();
    },
    historyTabClicked() {
      this.showHistoryTab = true;
    },
    handleUpdateTab(response) {
      let self = this
      setTimeout(function(){
        if (response == 'validations') {
          let index = 2;

          self.tabIndex = index
        }
      }, 2000);
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Policy',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    loadContent() {
      let self = this;
      piincHttp.get('policies/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.policy = response.data;
        self.loadTasks('update_basic_info')
        if (!self.isOwner(self.policy)) {
          self.goToList()
        }
        // Push content name in breadcrumb array
        self.breadcrumbItems.push({text: self.policy.name, active: true});
      }, function(error) {
        if (error.status == 404) {
          self.show404 = true
        }
        if (error.status == 403) {
          self.show403 = true
        }
      });
    },
    openDeleteAttachmentModal(object) {
      this.selectedFile = object
      this.showDeleteAttachmentModal = true
    },
    closeDeleteAttachmentModal () {
      this.selectedFile = {}
      this.showDeleteAttachmentModal = false;
      // Remove last item in breadcrumbs, because it will be added again on load content
      this.breadcrumbItems.pop();
      this.loadContent();
    },
    openDeleteLinkModal(object) {
      this.selectedLink = object
      this.showDeleteLinkModal = true
    },
    closeDeleteLinkModal () {
      this.selectedLink = {}
      this.showDeleteLinkModal = false;
      // Remove last item in breadcrumbs, because it will be added again on load content
      this.breadcrumbItems.pop();
      this.loadContent();
    },
    handleCategoryInput(input) {
      this.categoryInput = input;
    },
    handleLink() {
      let self = this;
      let formData = new FormData();

      if (!this.inputLink.startsWith('http')) {
        this.inputLink = 'http://' + this.inputLink
      }

      formData.append("url", this.inputLink);
      formData.append("name", this.inputLinkName);
      formData.append("model_type", this.policy.who_am_i);
      formData.append("model_id", this.policy.id);

      piincHttp.post('links', formData).then(function() {
        // Remove last item in breadcrumbs, because it will be added again on load content
        self.breadcrumbItems.pop();
        self.$toastr('success', self.$t('maintenance.policies.link_add_success'))
        self.loadContent()
        self.inputLink = ''
        self.inputLinkName = ''
        self.showInput = false
      }, function() {})
    },
    updateValidation(validation) {
      // Event emmited from validation component, update active validation
      this.policy.validation = validation
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove()
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    onFileChange(file) {
      let files = file.target.files;
      let self = this;
      let formData = new FormData();
      let sizeError = false;

      for (var i = 0; i < files.length; i++) {
        // Equal to 10 MB
        if (files[i].size > 10485760) {
          sizeError = true;
        }
        formData.append('document[]', files[i]);
      }

      formData.append("name", "documents");
      formData.append("model_type", this.policy.who_am_i);
      formData.append("model_id", this.policy.id);

      if (sizeError) {
        this.error = this.$t('system.file_size')
      } else {
        piincHttp.post('documents', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function() {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.$toastr('success', self.$t('maintenance.policies.media_add_success'))
          self.error = ''
          self.loadContent()
        }, function(error) {
          self.error = error.data.message;
          if (error.data.errors['document.0']) {
            self.error += ' ' + error.data.errors['document.0'][0]
          }
        })
      }
    },
    loadTasks(code) {
      let self = this;
      if (this.getClient.projectModule) {
        piincHttp.get('projects/me/tasks', { params:
          {
            per_page: 1,
            status: 'pending',
            model_type: this.policy.who_am_i,
            model_id: this.policy.id,
            code: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.myTasks = []
          if (response.data.items.length) {
            self.myTasks.push(response.data.items[0])
          }
        }, function() {});
      }
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return initialsAvatarUrl(user);
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-policy'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-policy'].hide()
    },
    confirmDeleteP(object) {
      let self = this;
      piincHttp.delete('policies/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.policies.delete.success'))
      }, function() {});
    },
    showDescriptionInputFunction() {
      this.showDescriptionInput = true;
      this.processEditDescription = this.policy.description;
    },
    formDescriptionSubmit() {
      let self = this;
      piincHttp.put('policies/' + this.policy.slug, {
        description: this.processEditDescription
      }).then(function(response) {
        self.showDescriptionInput = false;
        self.breadcrumbItems.pop();
        self.loadContent();
        self.$toastr('success', self.$t('maintenance.policies.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.error = error.data.message;
        }
      })
    },
    cancelDescriptionForm() {
      this.showDescriptionInput = false;
      this.processEditDescription = this.policy.description;
    },
    showEditPModal(object) {
      let self = this;
      this.error = ''
      if (object.users && object.users.length) {
        self.selectedUsers = [];
        _.forEach(object.users, function (user) {
          let matchedUser = _.cloneDeep(user)
          if (user.id == object.owner) {
            matchedUser.owner = true
          }
          self.selectedUsers.push(matchedUser)
        })
      }

      this.selectedOrganization = object.organization ? object.organization : {}

      if (object.tags_translated && object.tags_translated.length) {
        this.selectedTags = _.cloneDeep(object.tags_translated);
      }

      this.pForm.category = []
      if (object.category) {
        this.pForm.category = _.find(this.categoryItems, function(item) {
          return item.value === object.category;
        });
      }

      this.pForm.slug = object.slug;
      this.pForm.name = object.name;
      this.pForm.description = object.description;
      this.$refs['edit-policy'].show()
    },
    hideEditPModal() {
      this.error = ''
      this.categoryInput = ''
      this.loadTags();
      this.$refs['edit-policy'].hide()
    },
    editPFormSubmit(evt) {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      this.pForm.client = this.getClient.slug;

      this.pForm.tags = [];
      if (this.selectedTags && this.selectedTags.length) {
        for (var i = 0; i < this.selectedTags.length; i++) {
          this.pForm.tags.push(this.selectedTags[i].name_translated);
        }
      }

      let owners = []
      this.pForm.users = [];
      this.pForm.owner = [];
      let selectedOwner = false;
      if (this.selectedUsers && this.selectedUsers.length) {
        for (var i = 0; i < this.selectedUsers.length; i++) {
          this.pForm.users.push(this.selectedUsers[i].id);
          if (this.selectedUsers[i].owner) {
            selectedOwner = true;
            owners.push(this.selectedUsers[i].id)
          }
        }
      } else {
        selectedOwner = true;
      }

      if (owners.length) {
        this.pForm.owner = owners
      }

      this.pForm.category = this.pForm.category ? this.pForm.category.value : '';

      piincHttp.put('policies/' + this.pForm.slug, this.pForm).then(function(response) {
        self.hideEditPModal();
        self.$store.commit('storeUpdateContent', response.data);
        self.$emit('reloadContent', response.data);
        // If slug is not changed, just reload content
        if (response.data.slug === self.policy.slug) {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.loadContent();
          self.loadTasks('update_basic_info');
        } else {
          // If slug changed, reload state with new param
          self.$router.push({ name: 'policies.view', params: { slug: response.data.slug }})
        }
        self.buttonDisabled = false;
        self.$toastr('success', self.$t('maintenance.policies.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    updateFormCompany(item) {
      if (item && item.id) {
        this.pForm.organization_id = item.id
      } else {
        this.pForm.organization_id = ''
      }
    },
    addTag(newTag) {
      const tag = {
        name: newTag,
        translatable: newTag,
        name_translated: newTag
      }
      this.selectedTags.push(tag)
      this.newTags.push(tag)
    },
    removeUser(index) {
      let selectedItem = this.selectedUsers[index]
      selectedItem.owner = false;
      this.$set(this.selectedUsers, index, selectedItem)
      // Remove item from selected users array
      this.selectedUsers.splice(index, 1);
    },
    removeUserFromDropdown(item) {
      item.owner = false;
    },
    setOwner(index) {
      let selectedItem = this.selectedUsers[index]
      selectedItem.owner = !selectedItem.owner;
      this.$set(this.selectedUsers, index, selectedItem)
    },
    loadUsers(query) {
      let self = this;
      piincHttp.get('users', { params:
        {
          client: this.getClient.id,
          keyword: query ? query : undefined,
          per_page: 50
        }
      }).then(function(response) {
        self.users = response.data.items
        if (self.users && !self.users.length && !query) {
          self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
        }
      }, function() {});
    },
    loadTags(query) {
      let self = this;
      piincHttp.get('tags', { params:
        {
          client: this.getClient.slug,
          keyword: query ? query : undefined
        }
      }).then(function(response) {
        self.tags = response.data
        if (self.tags && !self.tags.length && !query) {
          self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
        }
        for (var i = 0; i < self.newTags.length; i++) {
          self.tags.push(self.newTags[i]);
        }
      }, function() {});
    }
  },
  created () {
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
      this.loadTags();
      this.loadUsers();
    }

    if (this.policy && this.policy.id) {
      this.loadTasks('update_basic_info');
    }
    this.loadGuides('update_basic_info');
  }
}
</script>
